import { type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { cx } from './cx.ts'

export type DetailTipProps = {
  /** Widoczny tekst przycisku, domyślnie „[szczegóły]” (na step.tutlo.com.pl też „TUTAJ.”). */
  label?: string
  /** Treść dymka: pełna klauzula, może zawierać link (klasa `text-tooltip-link`). */
  children: ReactNode
  /** Nadpisuje styl wyzwalacza — domyślny to violet link jak w HubSpocie. */
  className?: string
}

/**
 * „[szczegóły]” jak na step.tutlo.com.pl (HubSpot `hs-detail-link` + `hs-detail-tip`): violet link z podkreśleniem,
 * kursor `help`, ciemny dymek 11 px nad wierszem. Desktop: hover/focus; mobile: tap przełącza, tap poza zamyka.
 * Dymek pozycjonuje się względem najbliższego przodka `relative` (cały wiersz zgody), żeby nie wychodził poza ekran.
 */
export function DetailTip({ label = '[szczegóły]', children, className }: DetailTipProps) {
  const id = useId()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!open) return
    const close = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', close)
    return () => document.removeEventListener('pointerdown', close)
  }, [open])

  return (
    <span ref={ref} className="group">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false)
        }}
        className={cx(
          'cursor-help whitespace-nowrap underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-selected',
          className ?? 'text-selected',
        )}
      >
        {label}
      </button>
      <span
        id={id}
        role="tooltip"
        className={cx(
          'absolute bottom-full left-0 z-50 mb-sm max-h-[260px] w-full max-w-[360px] overflow-y-auto rounded-[10px] bg-tooltip px-md py-[10px] text-left text-[11px] font-normal leading-[1.5] whitespace-normal text-white shadow-tooltip transition-[opacity,visibility] duration-150 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100',
          open ? 'visible opacity-100' : 'invisible opacity-0',
        )}
      >
        {children}
      </span>
    </span>
  )
}
